import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
} from "@angular/core";

import { Shop } from "@hermes/api-model-shop";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { RouterService } from "@hermes/utils-generic/services/router";

import { DEFAULT_SECTIONS_TO_DISPLAY } from "../../constants/shop.constants";
import {
    DirectionLinkMode,
    SectionName,
    ShopInfoEvent,
} from "../../types/shop-info.type";

type SectionDisplayConfig = Record<SectionName, boolean>;

@Component({
    selector: "h-shop-info-details",
    templateUrl: "./shop-info-details.component.html",
    styleUrls: ["./shop-info-details.component.scss"],
    standalone: false,
})
export class ShopInfoDetailsComponent implements OnInit {
    @Input()
    public shop!: Shop;

    @Input()
    public headerLevelShopTitle: number = 3;

    /**
     * Number of meters between the current location and the shop.
     * Displayed under the direction icon if `directionLinkMode` is set to `icon`
     */
    @Input()
    public meters: number = 0;

    /**
     * Direction link to use if `directionLinkMode` is set to `icon` or `text`.
     */
    @Input()
    public directionUrl: string = "";

    @Input()
    public directionLinkMode: DirectionLinkMode = "none";

    @Input()
    public sectionsToDisplay: Set<SectionName> = DEFAULT_SECTIONS_TO_DISPLAY;

    @Output()
    public shopInfoDetailsEvents = new EventEmitter<ShopInfoEvent>();

    @Output()
    public shopInfoDirectionEvents = new EventEmitter<ShopInfoEvent>();

    public isDetailOpen = false;

    public shouldDisplaySectionsConfig: SectionDisplayConfig = {
        streetAddress1: false,
        phoneNumber: false,
        openingHours: false,
        additionalInformation: false,
        appointmentInStore: false,
    };

    public hiddenSections = new Set<SectionName>();

    constructor(
        private element: ElementRef<HTMLElement>,
        private routerService: RouterService,
        private changeDetector: ChangeDetectorRef,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public ngOnInit(): void {
        Object.keys(this.shouldDisplaySectionsConfig).forEach((key) => {
            const sectionName = key as SectionName;
            this.shouldDisplaySectionsConfig[sectionName] =
                this.sectionsToDisplay.has(sectionName) &&
                Boolean(this.shop[sectionName]);

            const shouldHideSection =
                !this.sectionsToDisplay.has(sectionName) &&
                Boolean(this.shop[sectionName]);
            // Appointment section can't be hidden, it is displayed by default or not present at all
            if (shouldHideSection && sectionName !== "appointmentInStore") {
                this.hiddenSections.add(sectionName);
            }
        });
    }

    public navigateToAppointment(shopUrl: string): void {
        this.shopInfoDetailsEvents.emit("appointment_in_store_page");
        this.routerService.spaRedirect(
            `${this.locale.urlPrefix}/${shopUrl}appointment/`,
        );
    }

    public toggleDetails(): void {
        this.shopInfoDetailsEvents.emit(
            this.isDetailOpen ? "close-details" : "more_details",
        );
        this.isDetailOpen = !this.isDetailOpen;

        this.hiddenSections.forEach((section) => {
            this.shouldDisplaySectionsConfig[section] =
                this.isDetailOpen && Boolean(this.shop[section]);
        });
        if (this.isDetailOpen) {
            this.changeDetector.detectChanges();

            const firstInitiallyHiddenSection =
                this.element.nativeElement.querySelector(
                    ".initially-hidden-section",
                );
            const focusTarget = firstInitiallyHiddenSection?.querySelector(
                "div",
            )?.firstElementChild as HTMLElement | undefined;
            focusTarget?.focus();
        }
    }
}
